import moment from 'moment-timezone';
import { Tooltip } from '@material-ui/core';

import { unixDateToText, uppercaseFirstLetter } from '../utils';
import warningIcon from '../common/Images/triangle_warning.svg';
import { CUSTOM_DETAILS_OPTIONS } from './EntityDetailsMapping';
import { EntityAttribute } from './EntityAttribute';

const customDetailsMapping = {
    [CUSTOM_DETAILS_OPTIONS.USER_PRINCIPAL_NAME]: {
        detailsElem: (entityDetails) => {
            let icon;
            if (
                'user_account_control' in entityDetails &&
                (entityDetails.user_account_control === null || entityDetails.user_account_control === undefined)
            ) {
                icon = (
                    <Tooltip
                        title={
                            <div>
                                Insufficient permissions to read: SID, SAM, GUID, UAC
                                <br />
                                Go to Silverfort Services in order to grant Read Permission.
                            </div>
                        }
                        enterDelay={100}
                        placement={'top'}
                        classes={{ tooltip: 'generic-tooltip' }}>
                        <img src={warningIcon} alt="warning icon" />
                    </Tooltip>
                );
            }
            return (
                <EntityAttribute
                    key={CUSTOM_DETAILS_OPTIONS.USER_PRINCIPAL_NAME}
                    label={'User Principal Name'}
                    value={entityDetails.identifier}
                    icon={icon}
                />
            );
        }
    },
    [CUSTOM_DETAILS_OPTIONS.DOMAIN]: {
        detailsElem: (entityDetails) => (
            <EntityAttribute
                key={CUSTOM_DETAILS_OPTIONS.DOMAIN}
                label={'Domain'}
                value={entityDetails.domain_name || 'N/A'}
            />
        )
    },
    [CUSTOM_DETAILS_OPTIONS.MFA]: {
        detailsElem: (entityDetails) => {
            const value = entityDetails.tokensCount
                ? `Yes, has ${entityDetails.tokensCount} MFA ${entityDetails.tokensCount === 1 ? 'token' : 'tokens'}`
                : 'No';
            return <EntityAttribute key={CUSTOM_DETAILS_OPTIONS.MFA} label={'MFA Enrollment'} value={value} />;
        }
    },
    [CUSTOM_DETAILS_OPTIONS.SAM]: {
        detailsElem: (entityDetails) => (
            <EntityAttribute key={CUSTOM_DETAILS_OPTIONS.SAM} label={'SAM Account'} value={entityDetails.user_name} />
        )
    },
    [CUSTOM_DETAILS_OPTIONS.EMAIL]: {
        detailsElem: (entityDetails) => (
            <EntityAttribute key={CUSTOM_DETAILS_OPTIONS.EMAIL} label={'Email'} value={entityDetails.email} />
        )
    },
    [CUSTOM_DETAILS_OPTIONS.LAST_PASSWORD_CHANGE]: {
        detailsElem: (entityDetails) => (
            <EntityAttribute
                key={CUSTOM_DETAILS_OPTIONS.LAST_PASSWORD_CHANGE}
                label={'Last Password Change'}
                value={unixDateToText(entityDetails.pwd_last_set)}
            />
        )
    },
    [CUSTOM_DETAILS_OPTIONS.TENANT]: {
        detailsElem: (entityDetails) => (
            <EntityAttribute key={CUSTOM_DETAILS_OPTIONS.TENANT} label={'Tenant Id'} value={entityDetails.tenant_id} />
        )
    },
    [CUSTOM_DETAILS_OPTIONS.APP_ID]: {
        detailsElem: (entityDetails) => (
            <EntityAttribute key={CUSTOM_DETAILS_OPTIONS.APP_ID} label={'App Id'} value={entityDetails.identifier} />
        )
    },
    [CUSTOM_DETAILS_OPTIONS.CREATED]: {
        detailsElem: (entityDetails) => {
            let updateDate = 'N/A';
            if (entityDetails.update_date) {
                const updateDateTime = moment.utc(entityDetails.update_date).local();
                updateDate = `${updateDateTime.format('L')}, ${updateDateTime.format('LTS')}`;
            }
            return <EntityAttribute key={CUSTOM_DETAILS_OPTIONS.CREATED} label={'Created'} value={updateDate} />;
        }
    },
    [CUSTOM_DETAILS_OPTIONS.UPDATED]: {
        detailsElem: (entityDetails) => {
            let updateDate = 'N/A';
            if (entityDetails.update_date) {
                const updateDateTime = moment.utc(entityDetails.update_date).local();
                updateDate = `${updateDateTime.format('L')}, ${updateDateTime.format('LTS')}`;
            }
            return <EntityAttribute key={CUSTOM_DETAILS_OPTIONS.UPDATED} label={'Updated'} value={updateDate} />;
        }
    },
    [CUSTOM_DETAILS_OPTIONS.STATUS]: {
        detailsElem: (entityDetails) => (
            <EntityAttribute
                key={CUSTOM_DETAILS_OPTIONS.STATUS}
                label={'Status'}
                value={entityDetails.status ? uppercaseFirstLetter(entityDetails.status.toLowerCase()) : 'N/A'}
            />
        )
    },
    [CUSTOM_DETAILS_OPTIONS.OPERATING_SYSTEM]: {
        detailsElem: (entityDetails) => (
            <EntityAttribute
                key={CUSTOM_DETAILS_OPTIONS.OPERATING_SYSTEM}
                label={'Operating System'}
                value={entityDetails.operating_system ? entityDetails.operating_system : 'N/A'}
            />
        )
    },
    [CUSTOM_DETAILS_OPTIONS.POLICIES]: {
        detailsElem: (entityDetails) => {
            const value =
                entityDetails.rules && entityDetails.rules.length
                    ? `Yes, ${entityDetails.rules.length} active ${
                          entityDetails.rules.length === 1 ? 'policy' : 'policies'
                      }`
                    : 'No';
            return <EntityAttribute key={CUSTOM_DETAILS_OPTIONS.POLICIES} label={'Active Policy'} value={value} />;
        }
    },
    [CUSTOM_DETAILS_OPTIONS.SERVICES]: {
        detailsElem: (entityDetails) => (
            <EntityAttribute
                key={CUSTOM_DETAILS_OPTIONS.SERVICES}
                label={'Services'}
                value={entityDetails.services ? entityDetails.services.length : 'N/A'}
            />
        )
    },
    [CUSTOM_DETAILS_OPTIONS.LAST_LOGON]: {
        detailsElem: (entityDetails) => (
            <EntityAttribute
                key={CUSTOM_DETAILS_OPTIONS.LAST_LOGON}
                label={'Last logon'}
                value={unixDateToText(entityDetails.last_logon_timestamp)}
            />
        )
    },
    [CUSTOM_DETAILS_OPTIONS.RISK_INDICATORS]: {
        dataElem: (entityData) => {
            if (!entityData?.RiskReasons) {
                return null;
            }
            const riskIndicators = entityData.RiskReasons.map((riskIndicatorObj) => riskIndicatorObj.entityTitle).join(
                ', '
            );

            return (
                <EntityAttribute
                    key={CUSTOM_DETAILS_OPTIONS.RISK_INDICATORS}
                    label={'Risk Indicators'}
                    valueClass="break-words"
                    value={riskIndicators}
                />
            );
        }
    },
    [CUSTOM_DETAILS_OPTIONS.MEMBERSHIPS]: {
        dataElem: (entityData) => {
            if (!entityData?.groups) {
                return null;
            }
            const memberships = entityData.groups.map((group) => (
                <div
                    title={group.name}
                    className={`col-start-2 text-15 text-ellipsis whitespace-nowrap overflow-x-hidden min-h-9 font-bold inline-block text-sf-primary-500 border mt-1 px-4 py-1 rounded border-solid border-sf-primary-500`}
                    key={group.name}>
                    {group.name}
                </div>
            ));
            return (
                <EntityAttribute key={CUSTOM_DETAILS_OPTIONS.MEMBERSHIPS} label="Member of" valueChild={memberships} />
            );
        }
    }
};

const EntityDetails = ({ entityDetails, entityData, customFieldsToShow = [] }) => {
    return (
        <>
            {customFieldsToShow.flatMap((field) => {
                if (!customDetailsMapping[field]) return [];
                return customDetailsMapping[field].dataElem
                    ? customDetailsMapping[field].dataElem(entityData)
                    : customDetailsMapping[field].detailsElem(entityDetails);
            })}
        </>
    );
};

export default EntityDetails;
